/**
 * AgentService.js
 *
 * Cognito authentication + API Gateway invocation with JWT authorizer.
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

// ─── CONFIGURATION ──────────────────────────────────────────────
const CONFIG = {
  COGNITO_USER_POOL_ID: "us-east-1_hBBZ9hyA6",
  COGNITO_CLIENT_ID: "23fs65sg3nn9adgupie6qpnfdj",
};
const BFF_ENDPOINT = "https://8bpqr2svs1.execute-api.us-east-1.amazonaws.com/prod/invoke"; // API Gateway invoke URL

// ─── AUTH ────────────────────────────────────────────────────────
const userPool = new CognitoUserPool({
  UserPoolId: CONFIG.COGNITO_USER_POOL_ID,
  ClientId: CONFIG.COGNITO_CLIENT_ID,
});

let cachedToken = null;

export async function signIn(username, password) {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: username, Pool: userPool });
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });
    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        cachedToken = session.getIdToken().getJwtToken();
        resolve(cachedToken);
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
        resolve({ newPasswordRequired: true, user, userAttributes });
      },
    });
  });
}

export async function completeNewPassword(user, newPassword, userAttributes) {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => {
        cachedToken = session.getIdToken().getJwtToken();
        resolve(cachedToken);
      },
      onFailure: (err) => reject(err),
    });
  });
}

export function getToken() {
  return cachedToken;
}

// ─── AGENT INVOCATION ────────────────────────────────────────────
let sessionId = null;
let inFlight = false;
export async function invokeAgent(prompt) {
  if (inFlight) {
    return "Request already in progress. Please wait.";
  }
  inFlight = true;
  try {
  const token = getToken();
  if (!token) {
    throw new Error("Not authenticated. Please sign in first.");
  }

  const payload = { prompt };
  if (sessionId) {
    payload.sessionId = sessionId;
  }

  const response = await fetch(BFF_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Agent invocation failed (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  if (data.sessionId) {
    sessionId = data.sessionId;
  }
  return data.result || data.message || "No response from agent.";
  } finally {
    inFlight = false;
  }
}